import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO head — comprehensive meta + structured data for every page.
 *
 * Renders: title, description, keywords, canonical, robots, Open Graph,
 * Twitter Cards, geo, JSON-LD (Organization+ProfessionalService, WebSite with
 * SearchAction, WebPage, BreadcrumbList, optional FAQPage, plus opt-in
 * Article / HowTo / Review / SoftwareApplication / DefinedTerm / ItemList /
 * Product+Offer / VideoObject), preconnect hints, theme-color.
 *
 * Used by every public page so we get a consistent SEO footprint.
 *
 * Props
 *   title           — required
 *   description     — required
 *   keywords        — comma-separated (optional)
 *   path            — page path with leading slash. Used for canonical + OG URLs
 *   ogImage         — full URL to a 1200×630 image
 *   ogType          — "website" | "article" (default "website")
 *   noIndex         — set true to prevent indexing
 *   lang            — page language code, default "en"
 *   locale          — full locale e.g. "en_US"
 *   author          — defaults to "AppFillip"
 *   breadcrumbs     — [{ name, item }]
 *   faqs            — [{ q, a }]
 *
 *   ── Opt-in structured data (any of these can be provided) ──
 *   article         — { headline, image, datePublished, dateModified?, authorName?, wordCount?, articleSection?, articleBody?, keywords? }
 *   howTo           — { name, description?, totalTime?, steps: [{ name, text }] }
 *   reviews         — [{ author, rating, body, datePublished? }]
 *   aggregateRating — { ratingValue, reviewCount, bestRating?, worstRating? } — applies to the WebPage's primary entity
 *   softwareApp     — { name, applicationCategory?, operatingSystem?, offers?: { price, priceCurrency } }
 *   definedTerm     — { name, description, inDefinedTermSet? }
 *   itemList        — { name?, items: [{ name, url, description? }] }
 *   product         — { name, description?, image?, brand?, offers: [{ name, price, priceCurrency }], aggregateRating? }
 *   video           — { name, description, thumbnailUrl, uploadDate, duration?, contentUrl?, embedUrl? }
 *   creativeWork    — full CreativeWork JSON-LD object (escape hatch)
 *
 *   structuredData  — array of additional raw JSON-LD objects (escape hatch)
 *   children        — extra Helmet children
 */

const SITE = "https://appfillip.com";
const DEFAULT_OG = `${SITE}/og/default.png`;

/* ─── Organization (merged with ProfessionalService) ──────── */
const ORG_LD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${SITE}/#organization`,
  name: "AppFillip",
  legalName: "Lumotis Digital Media Pvt. Ltd.",
  alternateName: "AppFillip · App Marketing Agency",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/favicon.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE}/favicon.png`,
  description:
    "Full-stack app marketing agency for iOS & Android — ASO, User Acquisition, Influencer, Retention, Creative & Analytics. 100+ apps ranked across 15+ markets.",
  foundingDate: "2017",
  founders: [{ "@type": "Person", name: "Rajesh Sharma" }],
  slogan: "The App Marketing Agency that takes apps from zero to category leader.",
  priceRange: "$$$",
  knowsAbout: [
    "App Store Optimization",
    "Mobile User Acquisition",
    "Influencer Marketing",
    "App Retention",
    "Creative Production",
    "Mobile Analytics",
    "Apple Search Ads",
    "Google App Campaigns",
    "Meta App Install Ads",
    "TikTok For Business",
  ],
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "India" },
    { "@type": "Country", name: "Germany" },
    { "@type": "Country", name: "France" },
    { "@type": "Country", name: "Spain" },
    { "@type": "Country", name: "Brazil" },
    { "@type": "Country", name: "Japan" },
    { "@type": "Country", name: "Singapore" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "Australia" },
    { "@type": "Country", name: "Canada" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Jaipur",
    addressRegion: "Rajasthan",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "info@appfillip.com",
      telephone: "+91-99505-10511",
      availableLanguage: ["en"],
      areaServed: ["US", "GB", "IN", "DE", "FR", "ES", "BR", "JP", "SG", "AE", "AU", "CA"],
    },
    {
      "@type": "ContactPoint",
      contactType: "billing",
      email: "billing@appfillip.com",
      availableLanguage: ["en"],
    },
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@appfillip.com",
      availableLanguage: ["en"],
    },
  ],
  sameAs: [
    "https://linkedin.com/company/appfillip",
    "https://twitter.com/appfillip",
    "https://instagram.com/appfillip",
    "https://youtube.com/@appfillip",
    "https://facebook.com/appfillip",
  ],
  // No aggregateRating here: Google treats a rating an organisation gives
  // itself as "self-serving" markup. Ratings belong on pages that display
  // the actual reviews (CaseStudyDetailPage passes `reviews`).
};

/* ─── WebSite + SearchAction (sitelinks search box) ───────── */
const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "AppFillip",
  alternateName: "AppFillip · App Marketing Agency",
  publisher: { "@id": `${SITE}/#organization` },
  inLanguage: "en-US",
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE}/blog?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

/* ─── Helper builders ─────────────────────────────────────── */

function ldArticle(article, url, ogImage) {
  if (!article) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: article.headline,
    image: article.image || ogImage,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      "@type": "Person",
      name: article.authorName || "AppFillip Editorial",
    },
    publisher: { "@id": `${SITE}/#organization` },
    description: article.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(article.wordCount ? { wordCount: article.wordCount } : {}),
    ...(article.articleSection ? { articleSection: article.articleSection } : {}),
    ...(article.articleBody ? { articleBody: article.articleBody } : {}),
    ...(article.keywords ? { keywords: article.keywords } : {}),
    inLanguage: "en-US",
  };
}

function ldHowTo(howTo) {
  if (!howTo || !Array.isArray(howTo.steps) || howTo.steps.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howTo.name,
    description: howTo.description,
    ...(howTo.totalTime ? { totalTime: howTo.totalTime } : {}),
    step: howTo.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

function ldReviews(reviews, aggregateRating, itemReviewedName, itemReviewedType = "Service") {
  if ((!reviews || reviews.length === 0) && !aggregateRating) return null;
  const obj = {
    "@context": "https://schema.org",
    "@type": itemReviewedType,
    name: itemReviewedName || "AppFillip",
  };
  if (aggregateRating) {
    obj.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(aggregateRating.ratingValue),
      reviewCount: String(aggregateRating.reviewCount),
      bestRating: String(aggregateRating.bestRating || 5),
      worstRating: String(aggregateRating.worstRating || 1),
    };
  }
  if (reviews && reviews.length > 0) {
    obj.review = reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
        worstRating: "1",
      },
      reviewBody: r.body,
      ...(r.datePublished ? { datePublished: r.datePublished } : {}),
    }));
  }
  return obj;
}

function ldSoftwareApp(app) {
  if (!app) return null;
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: app.name,
    applicationCategory: app.applicationCategory || "BusinessApplication",
    operatingSystem: app.operatingSystem || "Web",
    ...(app.offers
      ? {
          offers: {
            "@type": "Offer",
            price: String(app.offers.price ?? "0"),
            priceCurrency: app.offers.priceCurrency || "USD",
          },
        }
      : {}),
    publisher: { "@id": `${SITE}/#organization` },
  };
}

function ldDefinedTerm(t) {
  if (!t) return null;
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: t.name,
    description: t.description,
    ...(t.inDefinedTermSet
      ? {
          inDefinedTermSet: {
            "@type": "DefinedTermSet",
            name: t.inDefinedTermSet,
            url: `${SITE}/glossary`,
          },
        }
      : {}),
  };
}

function ldItemList(list, url) {
  if (!list || !Array.isArray(list.items) || list.items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${url}#itemlist`,
    ...(list.name ? { name: list.name } : {}),
    numberOfItems: list.items.length,
    itemListElement: list.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: it.url.startsWith("http") ? it.url : `${SITE}${it.url}`,
      name: it.name,
      ...(it.description ? { description: it.description } : {}),
    })),
  };
}

function ldProduct(p) {
  if (!p) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    ...(p.description ? { description: p.description } : {}),
    ...(p.image ? { image: p.image } : {}),
    brand: { "@type": "Brand", name: p.brand || "AppFillip" },
    ...(p.offers && p.offers.length > 0
      ? {
          offers: p.offers.map((o) => ({
            "@type": "Offer",
            name: o.name,
            price: String(o.price),
            priceCurrency: o.priceCurrency || "USD",
            availability: "https://schema.org/InStock",
            url: o.url || SITE,
          })),
        }
      : {}),
    ...(p.aggregateRating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(p.aggregateRating.ratingValue),
            reviewCount: String(p.aggregateRating.reviewCount),
            bestRating: "5",
            worstRating: "1",
          },
        }
      : {}),
  };
}

function ldVideo(v) {
  if (!v) return null;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.name,
    description: v.description,
    thumbnailUrl: v.thumbnailUrl,
    uploadDate: v.uploadDate,
    ...(v.duration ? { duration: v.duration } : {}),
    ...(v.contentUrl ? { contentUrl: v.contentUrl } : {}),
    ...(v.embedUrl ? { embedUrl: v.embedUrl } : {}),
    publisher: { "@id": `${SITE}/#organization` },
  };
}

export default function SEOHead({
  title,
  description,
  keywords,
  path = "/",
  ogImage = DEFAULT_OG,
  ogType = "website",
  noIndex = false,
  lang = "en",
  locale = "en_US",
  author = "AppFillip",
  breadcrumbs = null,
  faqs = null,
  // opt-in structured data
  article = null,
  howTo = null,
  reviews = null,
  aggregateRating = null,
  softwareApp = null,
  definedTerm = null,
  itemList = null,
  product = null,
  video = null,
  creativeWork = null,
  // escape hatch
  structuredData = [],
  children,
}) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${SITE}${cleanPath}`;
  const fullTitle = /\bAppFillip\b/i.test(title || "") ? title : `${title} | AppFillip`;

  const ldWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: fullTitle,
    description,
    url,
    inLanguage: locale.replace("_", "-"),
    isPartOf: { "@id": `${SITE}/#website` },
    about: { "@id": `${SITE}/#organization` },
    primaryImageOfPage: ogImage,
    ...(aggregateRating
      ? {
          mainEntity: {
            "@type": "Service",
            name: title,
            provider: { "@id": `${SITE}/#organization` },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(aggregateRating.ratingValue),
              reviewCount: String(aggregateRating.reviewCount),
              bestRating: String(aggregateRating.bestRating || 5),
              worstRating: String(aggregateRating.worstRating || 1),
            },
          },
        }
      : {}),
  };

  const ldBreadcrumbs =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: b.item.startsWith("http") ? b.item : `${SITE}${b.item}`,
          })),
        }
      : null;

  const ldFAQ =
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: locale.replace("_", "-"),
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  const allLD = [
    ORG_LD,
    WEBSITE_LD,
    ldWebPage,
    ldBreadcrumbs,
    ldFAQ,
    ldArticle(article, url, ogImage),
    ldHowTo(howTo),
    ldReviews(reviews, aggregateRating, title, "Service"),
    ldSoftwareApp(softwareApp),
    ldDefinedTerm(definedTerm),
    ldItemList(itemList, url),
    ldProduct(product),
    ldVideo(video),
    creativeWork,
    ...structuredData,
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Document */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="publisher" content="Lumotis Digital Media Pvt. Ltd." />
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex,nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <meta
        name="googlebot"
        content={noIndex ? "noindex,nofollow" : "index, follow, max-image-preview:large"}
      />
      <meta name="theme-color" content="#2196F3" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content="AppFillip" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={locale} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@appfillip" />
      <meta name="twitter:creator" content="@appfillip" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Preconnect to common asset hosts (improves LCP) */}
      <link
        rel="preconnect"
        href="https://customer-assets.emergentagent.com"
        crossOrigin="anonymous"
      />
      <link rel="dns-prefetch" href="https://customer-assets.emergentagent.com" />

      {/* JSON-LD */}
      {allLD.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}

      {children}
    </Helmet>
  );
}
